import { Button, DatePicker, Space, Table, TableProps, Typography, message } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getClassRecordAttendanceMonthly } from '~/services/class-record'
import { IClassRecordAttendanceMonthly } from '~/types/class-record'

import { Container } from './style'

const getUtcMonthTimestamp = (value: Dayjs) =>
  Date.UTC(value.year(), value.month(), 1, 0, 0, 0, 0)

const Index = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState<IClassRecordAttendanceMonthly[]>([])
  const [month, setMonth] = useState<Dayjs | null>(() => dayjs())

  const monthTimestamp = useMemo(() => {
    if (!month) return null
    return getUtcMonthTimestamp(month)
  }, [month])

  const fetchData = useCallback(() => {
    if (!monthTimestamp) {
      message.warning('请选择月份')
      return
    }
    setLoading(true)
    getClassRecordAttendanceMonthly(monthTimestamp)
      .then((res) => {
        setDataSource(res.data || [])
      })
      .catch((err) => {
        message.error(err?.message || '请求错误，请稍后再试')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [monthTimestamp])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSearch = useCallback(() => {
    fetchData()
  }, [fetchData])

  const columns = useMemo<TableProps<IClassRecordAttendanceMonthly>['columns']>(
    () => [
      {
        title: '教师ID',
        dataIndex: 'teacherId',
        key: 'teacherId',
        width: 140,
        fixed: 'left',
      },
      {
        title: '教师姓名',
        dataIndex: 'teacherName',
        key: 'teacherName',
        width: 160,
      },
      {
        title: '月份',
        dataIndex: 'month',
        key: 'month',
        width: 120,
      },
      {
        title: '老师时区',
        dataIndex: 'timeZone',
        key: 'timeZone',
        width: 120,
        render: (text) => text || 'UTC',
      },
      {
        title: '已到上课时间的确认预约数',
        dataIndex: 'confirmedAppointments',
        key: 'confirmedAppointments',
        width: 140,
      },
      {
        title: '结算课数（完成+学生缺勤）',
        dataIndex: 'settledClassCount',
        key: 'settledClassCount',
        width: 200,
        render: (_, record) => record.settledClassCount ?? record.validClassSessions,
      },
      {
        title: '完成',
        dataIndex: 'completedClassCount',
        key: 'completedClassCount',
        width: 100,
        render: (text) => text ?? '-',
      },
      {
        title: '缺勤次数',
        dataIndex: 'absentCount',
        key: 'absentCount',
        width: 120,
      },
      {
        title: '老师缺勤',
        dataIndex: 'teacherAbsentCount',
        key: 'teacherAbsentCount',
        width: 120,
      },
      {
        title: '学生缺勤',
        dataIndex: 'studentAbsentCount',
        key: 'studentAbsentCount',
        width: 120,
      },
      {
        title: '操作',
        dataIndex: 'action',
        key: 'action',
        width: 120,
        fixed: 'right',
        render: (_, record) => {
          if (!monthTimestamp) return '-'
          return (
            <Typography.Link
              onClick={() => {
                navigate(
                  `/home/class-record-statistics/details?teacherId=${record.teacherId}&month=${monthTimestamp}`,
                )
              }}
            >
              详情
            </Typography.Link>
          )
        },
      },
    ],
    [monthTimestamp, navigate],
  )

  return (
    <Container>
      <div className="top">
        <Space>
          <DatePicker
            picker="month"
            disabledDate={(date) => date.startOf('month').isAfter(dayjs())}
            allowClear={false}
            value={month}
            onChange={(value) => {
              setMonth(value)
            }}
          />
          <Button type="primary" loading={loading} onClick={onSearch}>
            查询
          </Button>
          <Typography.Text type="secondary">
            仅统计已到上课时间的课程。月份按老师个人时区划分；结算课数 = 完成 + 学生缺勤，与薪资发放口径一致。
          </Typography.Text>
        </Space>
      </div>
      <div className="table">
        <Table
          rowKey={(record) => `${record.teacherId}-${record.month}`}
          scroll={{ x: 1550, y: 500 }}
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
        />
      </div>
    </Container>
  )
}

export default memo(Index)
